import { type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useComunicado } from '../hooks/use-comunicados-list';
import { useComposerState } from '../hooks/use-composer-state';
import { useComposerPage } from '../hooks/use-composer-page';
import { ComposerActions } from './composer-actions';
import { ComposerHeader, ComposerGrid } from './composer-page-parts';
import type { Comunicado } from '../types-comunicados';

type LayoutProps = {
  existing: Comunicado | undefined;
  authorLabel: string;
};

function ComposerLayout({ existing, authorLabel }: LayoutProps): ReactNode {
  const { data, setData, update } = useComposerState(existing);
  const refresh = useComunicado(existing?.id).refetch;
  const page = useComposerPage({ existing, data, setData });
  return (
    <>
      <ComposerGrid
        data={data}
        update={update}
        existing={existing}
        authorLabel={authorLabel}
        onMediaChanged={() => void refresh()}
      />
      <ComposerActions
        isPublished={data.is_published}
        pending={page.pending}
        isEdit={!!existing}
        onSaveDraft={page.onSaveDraft}
        onPublish={page.onPublish}
        onDelete={page.onDelete}
      />
    </>
  );
}

function authorLabelOf(user: ReturnType<typeof useAuth>['user']): string {
  if (!user) return '';
  const full = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return full || user.ig_username;
}

export function ComunicadoComposerPage(): ReactNode {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: existing, isLoading } = useComunicado(id);
  if (id && isLoading) return <p className="text-text-tertiary">Cargando…</p>;
  return (
    <div className="mx-auto max-w-[1100px] space-y-lg pb-[6rem]">
      <ComposerHeader
        onBack={() => navigate('/admin/communications')}
        title={existing ? 'Editar comunicado' : 'Nuevo comunicado'}
      />
      <ComposerLayout existing={existing} authorLabel={authorLabelOf(user)} />
    </div>
  );
}
